import React, { PureComponent } from 'react';
import { Button, Divider, Space } from 'antd';
import { Flex } from '../../common/Flex';
import { ModalPanelProps } from '../DashboardModal';
import { ExtendedMonitoredTv, TvMeteoAlert, TVTimePeriodSelection } from '../../../model/SOL49';
import { ExtensiblePanelProps, ExtensiblePanelState } from '../../../common/panel/ExtensiblePanel';
import './TVTimePeriodSelectionPanel.less';
import { TVLoad } from '../../dashboard/network/flows/TVLoad';
import AppContext from '../../../common/context/AppContext';
import memoizeOne from 'memoize-one';
import { DCBSolverUtils } from '../../../common/util/DCBSolverUtils';
import { TimePeriod } from '../../../model/CORE';
import TimeUtils from '../../../common/util/TimeUtils';

interface TVTimePeriodSelectionPanelProps extends ModalPanelProps, ExtensiblePanelProps {
    monitoredTvs: Array<ExtendedMonitoredTv>;
    defaultTvSelection: Array<TVTimePeriodSelection>;
    onTvSelectionChange: (tvSelection: Array<TVTimePeriodSelection>) => void;
    tvMeteoAlerts?: { [key: string]: Array<TvMeteoAlert> };
}

interface TVTimePeriodSelectionPanelState extends ExtensiblePanelState {
    tvSelection: Array<TVTimePeriodSelection>;
}

export class TVTimePeriodSelectionPanel extends PureComponent<
    TVTimePeriodSelectionPanelProps,
    TVTimePeriodSelectionPanelState
> {
    public static INITIAL_WIDTH = '50em';

    constructor(props) {
        super(props);
        this.state = Object.assign({}, this.state, {
            tvSelection:
                this.props.defaultTvSelection ||
                DCBSolverUtils.getAutoTvSelection(
                    props.monitoredTvs,
                    TimeUtils.addMinutes(AppContext.getCurrentTime(), 30),
                ),
        });
    }

    componentDidUpdate(
        prevProps: Readonly<TVTimePeriodSelectionPanelProps>,
        prevState: Readonly<TVTimePeriodSelectionPanelState>,
    ) {
        if (this.props.defaultTvSelection !== prevProps.defaultTvSelection) {
            this.setState({ tvSelection: this.props.defaultTvSelection });
        }
    }

    handleAutoPeriodCalculation = () => {
        this.setState({
            tvSelection: DCBSolverUtils.getAutoTvSelection(
                this.props.monitoredTvs,
                TimeUtils.addMinutes(AppContext.getCurrentTime(), 30),
            ),
        });
    };

    handleFindSolution = () => {
        this.props.onTvSelectionChange?.(this.state.tvSelection);
        this.props.onClose?.();
    };

    handleSelectionChange = (monitoredTv: ExtendedMonitoredTv, selected: boolean) => {
        const updatedTvSelection = this.state.tvSelection.slice();
        const tvSelection = updatedTvSelection.find(tvSelection => tvSelection.tvId === monitoredTv.refLocation.id);
        tvSelection.selected = selected;
        this.setState({ tvSelection: updatedTvSelection });
    };

    getSelectionChangeHandlerByTv = memoizeOne((monitoredTvs: Array<ExtendedMonitoredTv>) => {
        const map = new Map();
        if (monitoredTvs) {
            monitoredTvs.forEach(monitoredTv => {
                map.set(monitoredTv.refLocation.id, selected => this.handleSelectionChange(monitoredTv, selected));
            });
        }
        return map;
    });

    handleSelectedPeriodChange = (monitoredTv: ExtendedMonitoredTv, selectedPeriod: TimePeriod) => {
        const updatedTvSelection = this.state.tvSelection.slice();
        const tvSelection = updatedTvSelection.find(tvSelection => tvSelection.tvId === monitoredTv.refLocation.id);
        tvSelection.timePeriod = selectedPeriod;
        this.setState({ tvSelection: updatedTvSelection });
    };

    getSelectedPeriodChangeHandlerByTv = memoizeOne((monitoredTvs: Array<ExtendedMonitoredTv>) => {
        const map = new Map();
        if (monitoredTvs) {
            monitoredTvs.forEach(monitoredTv => {
                map.set(monitoredTv.refLocation.id, selectedPeriod =>
                    this.handleSelectedPeriodChange(monitoredTv, selectedPeriod),
                );
            });
        }
        return map;
    });

    render() {
        const tvSet = 'LXXXFMP';
        const selectionChangeHandlerByTv = this.getSelectionChangeHandlerByTv(this.props.monitoredTvs);
        const selectedPeriodChangeHandlerByTv = this.getSelectedPeriodChangeHandlerByTv(this.props.monitoredTvs);
        const applyButtonDisabled = this.state.tvSelection.filter(tvSelection => tvSelection.selected).length === 0;

        return (
            <Flex className="query-panel" direction="vertical" style={{ fontWeight: 'bold' }}>
                <Flex className="modal-title" style={{ flex: '0 0 auto' }}>
                    <Flex direction="vertical">
                        <Flex style={{ fontWeight: 'bold', justifyContent: 'center' }}>
                            ADVANCED NETWORK ISSUE - Select TFV and Time Period
                        </Flex>
                    </Flex>
                </Flex>
                <Flex style={{ flex: '0 0 auto' }}></Flex>
                <Divider type="horizontal" />
                <Flex style={{ flexWrap: 'wrap' }}>
                    {this.props.monitoredTvs?.map(monitoredTv => {
                        const tvSelection = this.state.tvSelection.find(
                            tvSelection => tvSelection.tvId === monitoredTv.refLocation.id,
                        );
                        return (
                            <Flex key={`tv-${tvSelection.tvId}`} size="50%">
                                <TVLoad
                                    key={tvSet + '-' + monitoredTv.refLocation.id}
                                    tvName={monitoredTv.refLocation.id}
                                    vsName={tvSet}
                                    entryLoad={monitoredTv.loadValues}
                                    elementary={monitoredTv.elementary}
                                    occupancyLoad={monitoredTv.occupancyLoadValues}
                                    currentTime={AppContext.getCurrentTime()}
                                    /*monitoredMeasures={this.props.monitoredMeasures}*/
                                    activePeriods={monitoredTv.activePeriods}
                                    /*occupancyPeriods={occupancyPeriods}*/
                                    monitorable={monitoredTv.monitorable}
                                    hideMoreMenu={true}
                                    selected={tvSelection.selected}
                                    canSelectPeriod={true}
                                    defaultSelectedPeriod={tvSelection.timePeriod}
                                    onSelectedPeriodChange={selectedPeriodChangeHandlerByTv.get(
                                        monitoredTv.refLocation.id,
                                    )}
                                    onSelectionChange={selectionChangeHandlerByTv.get(monitoredTv.refLocation.id)}
                                    tvMeteoAlerts={this.props.tvMeteoAlerts?.[monitoredTv.refLocation.id]}
                                    // displayOccupancy={this.props.displayOccupancy}
                                    // displayOccupancyForTv={this.props.displayOccupancyForTv}
                                />
                            </Flex>
                        );
                    })}
                </Flex>
                <Divider type="horizontal" />
                <Flex style={{ alignSelf: 'flex-end', paddingTop: '1em', flex: '0 0 auto' }}>
                    <QueryPanelButtons
                        onClose={this.props.onClose}
                        onAuto={this.handleAutoPeriodCalculation}
                        applyButtonDisabled={applyButtonDisabled}
                        onApply={this.handleFindSolution}
                    />
                </Flex>
            </Flex>
        );
    }
}

interface QueryPanelButtonsProps extends ModalPanelProps {
    onAuto?(): void;
    applyButtonDisabled?: boolean;
    onApply?(): void;
}

class QueryPanelButtons extends PureComponent<QueryPanelButtonsProps> {
    handleCloseClick = () => {
        this.props.onClose?.();
    };
    handleAutoClick = () => {
        this.props.onAuto?.();
    };
    handleApplyClick = () => {
        this.props.onApply?.();
    };
    render() {
        return (
            <Space className="buttons-panel">
                <Button key="close" onClick={this.handleCloseClick}>
                    Close
                </Button>
                <Button key="auto" onClick={this.handleAutoClick}>
                    Auto
                </Button>
                <Button key="apply" disabled={this.props.applyButtonDisabled} onClick={this.handleApplyClick}>
                    Apply
                </Button>
            </Space>
        );
    }
}
